<template>
  <div class="hike-measurement-panel position-absolute card rounded p-3">
    <div class="measurement-header d-flex justify-content-between align-items-center mb-3">
      <h5 class="measurement-title m-0">{{ $t('hike_map.hike_measurement_panel.quick_measurement') }}</h5>
      <button
        type="button"
        class="btn-close btn-close-white"
        @click="closeMeasurement"
        :aria-label="$t('hike_map.hike_measurement_panel.close_measurement_tool')"
      ></button>
    </div>

    <!-- Routing Mode Selection (hidden when snapping to hike/recording line) -->
    <div v-if="context !== 'hike'" class="routing-mode-section mb-3">
      <label class="routing-mode-label mb-2">{{ $t('hike_map.hike_measurement_panel.routing_mode') }}</label>
      <div class="routing-mode-buttons d-flex gap-2">
        <button
          v-for="mode in routingModes"
          :key="mode.value"
          class="routing-mode-btn"
          :class="{ active: selectedRoutingMode === mode.value }"
          :title="mode.tooltip"
          @click="setRoutingMode(mode.value)"
        >
          <span class="material-icons">{{ mode.icon }}</span>
        </button>
      </div>
    </div>

    <div class="measurement-content">
      <div class="measurement-item d-flex justify-content-between align-items-center mb-1">
        <span class="measurement-label">{{ $t('hike_map.hike_measurement_panel.distance') }}</span>
        <span class="measurement-value">{{ distance }}</span>
      </div>

      <div class="measurement-item d-flex justify-content-between align-items-center" :class="{ 'mb-1': shouldShowHikeTime }">
        <span class="measurement-label">{{ $t('hike_map.hike_measurement_panel.elevation_gain') }}</span>
        <span class="measurement-value">{{ elevationGain }}</span>
      </div>

      <div v-if="shouldShowHikeTime" class="measurement-item d-flex justify-content-between align-items-center">
        <span class="measurement-label">{{ $t('hike_map.hike_measurement_panel.hike_time') }}</span>
        <span class="measurement-value">{{ hikeTime }}</span>
      </div>
    </div>

    <!-- Elevation Profile Chart -->
    <MeasurementElevationProfileChart v-if="showElevationProfile" />

    <!-- Create Hike Button -->
    <button
      v-if="showElevationProfile"
      class="btn-create-hike mt-2"
      @click="createHikeFromMeasurement"
    >
      <span class="material-icons">add_location_alt</span>
      {{ $t('hike_map.hike_measurement_panel.create_hike') }}
    </button>

    <hr class="mt-3 mb-2">

    <div class="measurement-instructions">
      <small class="text-muted">
        {{ $t('hike_map.hike_measurement_panel.click_on_the_route_line_to') }}
      </small>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapGetters, mapActions } from 'vuex';
import MeasurementElevationProfileChart from './MeasurementElevationProfileChart.vue';
import { snapPoints } from '../../../utils/models/Graphooper';
import { MarkerFeature, MarkerGeometry, MarkerProperties } from '../../../utils/models/MarkerFeature';
import { hiikerElevation } from '../../../utils/helpers/HiikerElevationApiHelper';
import { i18n } from '../../../i18n';

export default defineComponent({
  name: 'HikeMeasurementPanel',

  props: {
    context: {
      type: String,
      default: 'hike' // 'hike' for hike planner, 'main' for main map
    }
  },

  components: {
    MeasurementElevationProfileChart
  },

  data() {
    return {
      selectedRoutingMode: 'hike', // Default to hiking mode
      routingModes: [
        { value: 'straight', icon: 'block', get tooltip() { return i18n.global.t('hike_map.hike_measurement_panel.draw_straight_lines'); } },
        { value: 'hike', icon: 'hiking', get tooltip() { return i18n.global.t('hike_map.hike_measurement_panel.snap_to_hiking_paths'); } },
        { value: 'bike', icon: 'directions_bike', get tooltip() { return i18n.global.t('hike_map.hike_measurement_panel.snap_to_bike_routes'); } },
        { value: 'car', icon: 'directions_car', get tooltip() { return i18n.global.t('hike_map.hike_measurement_panel.snap_to_roads'); } }
      ]
    };
  },

  computed: {
    ...mapGetters('hikeMeasurement', [
      'measuredDistance',
      'measuredElevationGain',
      'measuredHikeTime',
      'isActive',
      'measuredGeoJson'
    ]),

    distance() {
      return this.measuredDistance || '0 m';
    },

    elevationGain() {
      return this.measuredElevationGain || '0 m';
    },

    hikeTime() {
      return this.measuredHikeTime || '~ ' + this.$t('hike_map.hike_measurement_module.count_mins', { count: 0 });
    },

    // Only show hike time for hiking and straight line modes, not for bike or car
    shouldShowHikeTime() {
      return this.selectedRoutingMode === 'hike' || this.selectedRoutingMode === 'straight';
    },

    // Show elevation profile only when we have valid measurement data
    showElevationProfile() {
      return this.measuredGeoJson &&
             this.measuredGeoJson.geometry &&
             this.measuredGeoJson.geometry.coordinates &&
             this.measuredGeoJson.geometry.coordinates.length > 1;
    }
  },

  methods: {
    ...mapActions('hikeMeasurement', ['stopMeasurementMode']),

    setRoutingMode(mode) {
      this.selectedRoutingMode = mode;

      // Update the global routing mode for the main map measurement system
      if (this.context === 'main' && window.mainMapMeasurementMode) {
        window.mainMapRoutingMode = mode;

        // If we have two markers, recreate the route with the new mode
        const markers = window.mainMapMeasurementMarkers || [];
        if (markers.length === 2) {
          this.recreateRouteWithNewMode(markers);
        }
      }
    },

    async recreateRouteWithNewMode(markers) {
      if (markers.length !== 2) return;

      // Clear ALL existing measurement lines before creating new route
      // Remove main-map-measurement layer
      if (window.removeMainMapMeasurementLine) {
        window.removeMainMapMeasurementLine();
      }

      // Also remove hike-measurement layer (from style changes)
      const map = window.map;
      if (map) {
        if (map.getLayer('hike-measurement')) {
          map.removeLayer('hike-measurement');
        }
        if (map.getSource('hike-measurement')) {
          map.removeSource('hike-measurement');
        }
      }

      const marker1 = markers[0];
      const marker2 = markers[1];

      const marker1Location = marker1.getLngLat();
      const marker2Location = marker2.getLngLat();

      // Create temporary marker features for GraphHopper
      const startMarker = new MarkerFeature(
        new MarkerGeometry([marker1Location.lng, marker1Location.lat]),
        new MarkerProperties('temp-start', false, Date.now().toString(), 0)
      );

      const endMarker = new MarkerFeature(
        new MarkerGeometry([marker2Location.lng, marker2Location.lat]),
        new MarkerProperties('temp-end', false, Date.now().toString(), 0)
      );

      try {
        if (this.selectedRoutingMode === 'straight') {
          // Use hiikerElevation to get elevation data for straight line mode
          const lineString = await hiikerElevation(startMarker, endMarker);

          // Convert the LineString to MultiLineString format expected by measurement tool
          const multiLineStringFeature = {
            type: 'Feature',
            geometry: {
              type: 'MultiLineString',
              coordinates: [lineString.lineStringGeometry.coordinates]
            },
            properties: {
              routeStyle: 'route'
            }
          };

          // Update the measureable GeoJSON with the elevation-enhanced straight line
          const updatedMeasureableGeoJson = {
            type: 'FeatureCollection',
            features: [multiLineStringFeature]
          };

          this.$store.commit('hikeMeasurement/setMeasureableGeoJson', updatedMeasureableGeoJson);
          console.log('Elevation-enhanced straight line route recreated for measurement');
        } else {
          // Use GraphHopper to create a route between the markers with the selected mode
          const lineString = await snapPoints(startMarker, endMarker, this.selectedRoutingMode);

          // Convert the LineString to MultiLineString format expected by measurement tool
          const multiLineStringFeature = {
            type: 'Feature',
            geometry: {
              type: 'MultiLineString',
              coordinates: [lineString.lineStringGeometry.coordinates]
            },
            properties: {
              routeStyle: 'route'
            }
          };

          // Update the measureable GeoJSON with the GraphHopper route using mutation
          const updatedMeasureableGeoJson = {
            type: 'FeatureCollection',
            features: [multiLineStringFeature]
          };

          this.$store.commit('hikeMeasurement/setMeasureableGeoJson', updatedMeasureableGeoJson);
          console.log(`GraphHopper route recreated with mode: ${this.selectedRoutingMode}`);
        }

        // Update the measurement line
        if (window.updateMainMapMeasurementLine) {
          window.updateMainMapMeasurementLine();
        }
      } catch (error) {
        console.error('Error recreating route:', error);

        // Fallback: create a direct line between points
        const directLineFeature = {
          type: 'Feature',
          geometry: {
            type: 'MultiLineString',
            coordinates: [[
              [marker1Location.lng, marker1Location.lat],
              [marker2Location.lng, marker2Location.lat]
            ]]
          },
          properties: {
            routeStyle: 'route'
          }
        };

        const fallbackMeasureableGeoJson = {
          type: 'FeatureCollection',
          features: [directLineFeature]
        };

        this.$store.commit('hikeMeasurement/setMeasureableGeoJson', fallbackMeasureableGeoJson);
      }
    },

    closeMeasurement() {
      if (this.context === 'main') {
        // For main map, use the global cleanup method
        if (window.stopMainMapMeasurementMode) {
          window.stopMainMapMeasurementMode();
        } else {
          // Fallback to standard Vuex action
          this.stopMeasurementMode();
        }
      } else {
        // For hike planner, use standard Vuex action
        this.stopMeasurementMode();
      }
    },

    createHikeFromMeasurement() {
      if (!this.measuredGeoJson || !this.measuredGeoJson.geometry) {
        console.error('No measurement data available');
        return;
      }

      // Create a FeatureCollection with the measured LineString
      const featureCollection = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: this.measuredGeoJson.geometry.coordinates
          },
          properties: {
            routeStyle: 'route'
          }
        }]
      };

      // Store in localStorage for the planner to pick up
      try {
        localStorage.setItem('hikerPlannerImport', JSON.stringify(featureCollection));

        // Navigate to the hike planner
        window.location.href = '/hike-planner';
      } catch (error) {
        console.error('Error storing measurement data:', error);
        window.alert(this.$t('hike_map.hike_measurement_panel.unable_to_create_hike'));
      }
    }
  }
});
</script>

<style scoped>
.hike-measurement-panel {
  background-color: rgba(32, 32, 32, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  top: 80px;
  right: 2%;
  min-width: 280px;
  z-index: 1000;
  backdrop-filter: blur(10px);
  color: #fff;
}

.measurement-header {
  padding-bottom: 0.75rem;
}

.measurement-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
}

.measurement-item {
  padding: 0.25rem 0;
}

.measurement-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.measurement-value {
  font-weight: 600;
  color: #4CAF50; /* Green color for measurement values */
}

.measurement-instructions {
  font-size: 0.8rem;
  line-height: 1.3;
}

.btn-close {
  filter: invert(0);
  opacity: 0.7;
}

.btn-close:hover {
  opacity: 1;
}

/* Routing Mode Styles */
.routing-mode-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 0.75rem;
}

.routing-mode-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  display: block;
}

.routing-mode-buttons {
  flex-wrap: wrap;
}

.routing-mode-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.routing-mode-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.routing-mode-btn.active {
  background: #ffd600;
  color: #202020;
  font-weight: 700;
}

/* Create Hike Button - tertiary style */
.btn-create-hike {
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: none;
  padding: 0.25rem 0;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: color 0.2s;
}

.btn-create-hike:hover {
  color: #ffd600;
}

.btn-create-hike .material-icons {
  font-size: 0.95rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .hike-measurement-panel {
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    min-width: auto;
    border-radius: 16px 16px 0 0;
    margin: 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
    border-bottom: none;
    max-height: 50vh;
    overflow-y: auto;
    z-index: 10000;
  }

  .measurement-header {
    top: 0;
    z-index: 10;
  }

  .measurement-content {
    padding-bottom: 20px;
  }
}
</style>
